// components/slides/SlidesEditor.tsx
'use client';
import { SlidesWelcomeButton } from './SlidesWelcome';
import { SlidesTourButton } from './SlidesTour';

// Slides editor shell = the CENTER CANVAS ONLY. Portal provides both side
// panels now (left = chat, right = SlidesRightSidebar in the shared right
// ResizablePanel), so this component renders just the black stage: the
// single-slide canvas (or the grid view), the Studio-style vertical toolbar
// (SlidesToolbar, absolute canvas-left), and minimal floating chrome ,
// back-to-dashboard + deck name (top-left) and zoom / fit / save dot
// (top-right) as frosted bg-white/10 backdrop-blur chips. All state lives in useSlidesStore.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Download, Minus, PanelLeft, PanelRight, Plus } from 'lucide-react';
import type { SlideDeckDTO } from '#types/types';
import { useSlidesStore } from './slidesStore';
import { useKernel } from '@/kernel/Kernel';
import { ZOOM_MAX, ZOOM_MIN, deckDisplayName } from './slidesUi';
import { reviewExportBaseName } from '#lib/slides/reviewStatus';
import { BarButton } from '@/components/BottomBar';
import SlidesCanvas from './SlidesCanvas';
import SlidesToolbar from './SlidesToolbar';

/** "Working on it…" chip, the List-loop telemetry pattern brought to Slides.
 *  Driven by the SESSION's live state (LLM streaming via chatState, or a script
 *  executing via runningScriptId), so it stays up across the WHOLE multi-pass
 *  review, including long thinking gaps between auto-recall turns, instead of
 *  dying 6 s after the last command like the old heartbeat glow. A short linger
 *  bridges the script-end → next-turn micro-gap so the loop always reads as
 *  on-the-go; the deck's own `agentActiveAt` heartbeat rides along as a
 *  fallback for the first seconds of a turn. */
const SaveDot = ({ projectId }: { projectId: string }) => {
  const saveState = useSlidesStore((s) => s.saveStates[projectId]);
  const status = saveState?.status ?? 'idle';
  // Only ever visible on FAILURE. "Saved" and "Saving…" are noise on a canvas
  // you are trying to read; a save that silently failed is lost work.
  if (status !== 'error') return null;
  const color = '#FF7369';
  const label = 'Save failed';
  return (
    <div
      className="flex min-h-7 min-w-7 shrink-0 items-center justify-center gap-2 rounded-md bg-white/10 px-2 backdrop-blur"
      title={saveState?.error ?? label}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
      {saveState?.conflict && <ReloadSavedDeck projectId={projectId} />}
    </div>
  );
};

const ReloadSavedDeck = ({ projectId }: { projectId: string }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const reload = async () => {
    if (loading || !window.confirm('Load the saved version? Unsaved edits in this tab will be discarded. Cancel to keep your edits.')) return;
    setLoading(true);
    setError(false);
    try {
      setError(!(await useSlidesStore.getState().loadDeck(projectId, true)));
    } finally {
      setLoading(false);
    }
  };
  return <span className="text-xs text-[#FFB4AB]">
    <button type="button" disabled={loading} onClick={() => void reload()} className="whitespace-nowrap underline">{loading ? 'Loading…' : 'Load saved version'}</button>
    {error && <span role="alert"> Could not reload, or new edits were made. Your edits are kept; retry.</span>}
  </span>;
};

/**
 * Windows/macOS both reject these in a filename, and the browser silently
 * mangles the download when `a.download` contains one. The deck name comes from
 * a user-uploaded file, so it can contain anything.
 */
const safeFileName = (name: string): string =>
  name.replace(/["*/:<>?\\|]/g, '-').replace(/\s+/g, ' ').trim() || 'deck';

/**
 * Show/hide one of the shared side panels. Reuses the kernel's existing
 * toggleLeftPanel / toggleRightPanel (the same handles BottomBar drives) rather
 * than reaching for the ResizablePanel refs directly.
 */
const PanelToggle = ({
  side,
  label,
  onClick,
}: {
  side: 'left' | 'right';
  label: string;
  onClick: () => void;
}) => {
  const Icon = side === 'left' ? PanelLeft : PanelRight;
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-white/10 text-white/80 backdrop-blur transition-colors hover:bg-white/15 hover:text-white"
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
};

type ExportMode = 'annotated' | 'clean';

const EXPORT_OPTIONS: { mode: ExportMode; label: string; hint: string }[] = [
  {
    mode: 'annotated',
    label: 'Review PDF',
    hint: 'Callouts and validation card',
  },
  {
    mode: 'clean',
    label: 'Clean deck',
    hint: 'Original pages, no review markup',
  },
];

/** A presentation has no review markup: its one deliverable is every page as it stands. */
const PRESENTATION_EXPORT: { mode: ExportMode; label: string; hint: string }[] = [{ mode: 'annotated', label: 'PDF', hint: 'Every page as it stands' }];

/**
 * Export chip + menu (a top-bar action, not a right-bar control).
 *
 * Two deliverables share one pipeline: the annotated review PDF, and the
 * tenant's deck with the markup off. The menu follows the SourceModeChip
 * pattern (aria-haspopup / role=menu / Escape + outside click) rather than
 * inventing a second dropdown idiom.
 *
 * The filename is set HERE, not from Content-Disposition: the anchor's
 * `download` attribute overrides the header, so without this both files land in
 * ~/Downloads under the same name and the browser appends " (1)".
 *
 * Takes the projectId, not the deck: the deck is only ever needed at click
 * time, and holding it as a prop would re-render the chip on every document
 * mutation of a review round and name the file from a stale reviewRound.
 */
export const ExportChip = ({ projectId }: { projectId: string }) => {
  const conflict = useSlidesStore((s) => s.saveStates[projectId]?.conflict);
  const isPresentation = useSlidesStore((s) => s.decks[projectId]?.doc.kind === 'presentation');
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState<ExportMode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const exportLock = useRef(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (wrapRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleExport = async (mode: ExportMode) => {
    // One export at a time: a second activation must not fire a second request.
    if (exportLock.current) return;
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    const current = useSlidesStore.getState().decks[projectId];
    const deck = current ? structuredClone(current) : null;
    if (!deck) {
      setOpen(false);
      setError('The deck is unavailable. Reopen it and retry export.');
      return;
    }
    exportLock.current = true;
    setOpen(false);
    setRunning(mode);
    setError(null);
    try {
      const saved = await useSlidesStore.getState().saveDeckNow(projectId, deck);
      if (!saved) throw new Error(useSlidesStore.getState().saveStates[projectId]?.error ?? 'The deck changed while preparing the PDF. Retry export.');
      const res = await fetch('/api/slides/export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId, mode, expectedVersion: deck.version }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message ?? `Export failed (${res.status}). Retry export.`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const base = safeFileName(deckDisplayName(deck));
      a.download =
        mode === 'clean' || deck.doc.kind === 'presentation' ? `${base}.pdf` : `${reviewExportBaseName(deck) ?? `${base} - RDM review R${deck.reviewRound}`}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Export failed. Retry export.');
    } finally {
      exportLock.current = false;
      setRunning(null);
    }
  };

  const label = running
    ? running === 'clean'
      ? 'Exporting clean…'
      : 'Exporting review…'
    : error
      ? 'Retry'
      : 'Export';

  return (
    <div ref={wrapRef} className="relative shrink-0">
      <button
        ref={triggerRef}
        data-slides-tour="export"
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={!!running}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-full text-[#B4B4B4] transition-colors hover:bg-white/[0.08] hover:text-[#ECECEC] disabled:opacity-60 ${running || error ? 'px-3' : 'w-8'}`}
        style={{ fontSize: 14 }}
        aria-label={label}
        title={error ?? 'Export deck to PDF'}
      >
        <Download size={18} aria-hidden="true" />
        {running || error ? label : null}
      </button>

      {error && <div role="alert" className="absolute right-0 top-10 z-[9999] w-72 rounded-[16px] border border-white/[0.06] bg-[#353535] p-3 text-[#F28B82]" style={{ fontSize: 13 }}>{error}
        {conflict && <ReloadSavedDeck projectId={projectId} />}
      </div>}

      {open && (
        <div
          role="menu"
          aria-label="Export deck"
          className="absolute right-0 top-10 z-[9999] w-64 rounded-[20px] border border-white/[0.06] bg-[#353535] p-1.5 shadow-[0_18px_52px_rgba(0,0,0,0.5)]"
        >
          {(isPresentation ? PRESENTATION_EXPORT : EXPORT_OPTIONS).map((opt) => (
            <button
              key={opt.mode}
              type="button"
              role="menuitem"
              onClick={() => void handleExport(opt.mode)}
              className="flex w-full flex-col items-start gap-0.5 rounded-[12px] px-3 py-2 text-left transition-colors hover:bg-white/[0.08] focus:bg-white/[0.08] focus:outline-none"
            >
              <span className="text-[#ECECEC]" style={{ fontSize: 14, lineHeight: '20px' }}>{opt.label}</span>
              <span className="text-[#AFAFAF]" style={{ fontSize: 12, lineHeight: '16px' }}>{opt.hint}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Zoom island, bottom-right, the same control Studio has, built from Studio's
 * own BarButton so the two canvases look and behave alike rather than each
 * inventing a chip. The reviewer asked for Studio's zoom; Studio's is not a
 * standalone component (it lives inside BottomBar and is wired to the kernel
 * workspace), so the reusable part is the button primitive and the island.
 *
 * Steps by the SAME 1.4x factor as the wheel and as kernel/useViewports, so
 * clicking + once and scrolling one notch do the same thing.
 */
const ZOOM_STEP = 1.4;

// Same baseline and island as SlidesToolbar (bottom: 16px, #333333, p-1,
// rounded-lg) so the two bottom controls sit on one line rather than a few
// pixels apart.
const ZOOM_BAR_STYLE: React.CSSProperties = {
  position: 'absolute',
  bottom: '16px',
  right: '24px',
  backgroundColor: '#333333',
  // Same family as the top chrome's z-[470]: the validation stamp (450) and
  // its handles (460) swallowed the zoom island past ~200% zoom, leaving no
  // way to zoom back out.
  zIndex: 470,
};

const stopEvent = (e: React.SyntheticEvent) => e.stopPropagation();

const SlidesZoomBar = ({ projectId }: { projectId: string }) => {
  const zoom = useSlidesStore((s) => s.runtime[projectId]?.zoomLevel) ?? 1;

  // BarButton is React.memo'd, and this bar re-renders on every zoomLevel
  // change (one per wheel notch), so the handlers are read off the store
  // instead of closing over `zoom`: depending only on projectId keeps them
  // stable and the three buttons out of the re-render.
  const setZoom = useCallback(
    (next: number) => {
      const { patchRuntime } = useSlidesStore.getState();
      patchRuntime(projectId, { zoomLevel: Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next)) });
    },
    [projectId],
  );
  const zoomOut = useCallback(() => {
    setZoom((useSlidesStore.getState().runtime[projectId]?.zoomLevel ?? 1) / ZOOM_STEP);
  }, [projectId, setZoom]);
  const zoomIn = useCallback(() => {
    setZoom((useSlidesStore.getState().runtime[projectId]?.zoomLevel ?? 1) * ZOOM_STEP);
  }, [projectId, setZoom]);
  const zoomReset = useCallback(() => setZoom(1), [setZoom]);

  return (
    <div
      className="flex flex-row items-center gap-1 rounded-lg p-1 pointer-events-auto"
      style={ZOOM_BAR_STYLE}
      onMouseDown={stopEvent}
      onDoubleClick={stopEvent}
      id="slides-zoom-bar"
    >
      {/* A very narrow stage keeps only the percentage (click fits the slide); the wheel still zooms. */}
      <BarButton onClick={zoomOut} tooltipText="Zoom Out" icon={Minus} className="@max-[459px]/stage:hidden" />
      <BarButton onClick={zoomReset} tooltipText="Fit slide to stage" className="min-w-[50px]">
        <span className="text-xs tabular-nums">{Math.round(zoom * 100)}%</span>
      </BarButton>
      <BarButton onClick={zoomIn} tooltipText="Zoom In" icon={Plus} className="@max-[459px]/stage:hidden" />
    </div>
  );
};

const SlidesEditor = ({ deck }: { deck: SlideDeckDTO }) => {
  const { kernel } = useKernel();
  const pid = deck.projectId;
  const runtime = useSlidesStore((s) => s.runtime[pid]);
  const addPage = useSlidesStore((s) => s.addPage);
  const focusPage = useSlidesStore((s) => s.focusPage);

  const page =
    deck.doc.pages.find((p) => p.id === runtime?.activePageId) ?? deck.doc.pages[0];

  // Undo/redo, at EDITOR level rather than on the canvas.
  //
  // The canvas's key handler is scoped to the focused page and its selection,
  // which is right for Delete and wrong for this: undo has to reach a change
  // made on a page you have since navigated away from, and has to work while
  // the pointer is over the page rail or the right bar.
  //
  // Cmd/Ctrl+Z, and both spellings of redo - Shift+Z is the Mac/Figma one,
  // Ctrl+Y the Windows one, and people arrive here with both in their fingers.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const key = e.key.toLowerCase();
      if (key !== 'z' && key !== 'y') return;
      // Never steal the browser's own undo from a text field: inside the chat
      // composer or a note being typed, Cmd+Z means "undo my typing".
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'TEXTAREA' || t.tagName === 'INPUT' || t.isContentEditable)) return;
      e.preventDefault();
      const store = useSlidesStore.getState();
      if (key === 'y' || e.shiftKey) store.redo(pid);
      else store.undo(pid);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pid]);

  return (
    <div data-slides-tour-project={pid} className="@container/stage relative flex h-full w-full flex-col bg-black">
      {/* Top-LEFT chrome: the panel toggles, then Export.
          It used to sit top-right, which collides with the app's own top-right
          header (credits / notifications / avatar) as soon as the right panel is
          collapsed and the stage spans the full window - Export landed on top of
          the credit counter. Top-left has nothing above it in any panel state.

          The toggles are here because when both panels are collapsed there is
          NOTHING left to click: the right ResizablePanel collapses to
          collapsedWidthPx={0} (Portal.tsx), so it disappears with no handle, and
          the left one leaves an 8px strip nobody finds. That is a dead end - the
          deck is open and the reviewer can neither chat nor reach the pages.

          Zoom lives in the bottom-right island like Studio's. The save indicator
          stays but ONLY when a save has FAILED - a quiet dot is clutter, an
          unnoticed failed save loses a reviewer's work. */}
      {/* Canvas isolates page layers so dense annotations and stamp handles
          cannot overtake these controls when the page scrolls beneath them. */}

      {/* Panel toggles live at the BOTTOM-left, with the other persistent stage
          controls (toolbar bottom-centre, zoom island bottom-right) rather than
          in the top chrome, which is for actions on the deck. */}
      <div className="absolute bottom-3 left-3 z-[470] flex items-center gap-1.5">
        <PanelToggle
          side="left"
          label="Show or hide the chat"
          onClick={() => kernel.current?.toggleLeftPanel()}
        />
        <PanelToggle
          side="right"
          label="Show or hide the pages panel"
          onClick={() => kernel.current?.toggleRightPanel()}
        />
      </div>

      {page ? (
        <>
          <SlidesCanvas deck={deck} page={page} />
          {/* Studio-style vertical toolbar, canvas-left (absolute in this
              relative stage container). */}
          <SlidesToolbar projectId={pid} />
          {/* Zoom island, bottom-right, same position and control as Studio's. */}
          <SlidesZoomBar projectId={pid} />
        </>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center gap-3">
          <div className="text-xs text-white/40">This deck has no slides yet.</div>
          <button
            type="button"
            onClick={() => {
              const created = addPage(pid);
              if (created) focusPage(pid, created.id);
            }}
            className="rounded-lg border border-[#333] bg-[#202020] px-3 py-1.5 text-xs font-medium text-white/90 transition-colors hover:bg-[#2A2A2A]"
          >
            Add a blank slide
          </button>
        </div>
      )}
    </div>
  );
};

/** ChatGPT canvas corner: three quiet glyphs, their names in tooltips. Drawn in the portal's top
 *  bar, on the back arrow's row (owner, 2026-10-06), since the stage clips anything above it. */
export const SlidesCornerActions = ({ projectId }: { projectId: string }) => (
  <div className="flex items-center gap-1">
    <SaveDot projectId={projectId} />
    <SlidesWelcomeButton iconOnly />
    <SlidesTourButton iconOnly />
    <ExportChip projectId={projectId} />
  </div>
);

export default SlidesEditor;
